import React, { useState } from "react"
import { makeStyles } from "@material-ui/core/styles"
import Table from "@material-ui/core/Table"
import TableBody from "@material-ui/core/TableBody"
import TableCell from "@material-ui/core/TableCell"
import TableHead from "@material-ui/core/TableHead"
import TableRow from "@material-ui/core/TableRow"
import Paper from "@material-ui/core/Paper"
import Tooltip from "@material-ui/core/Tooltip"
import Pagination from "@material-ui/lab/Pagination"
import IconButton from "@material-ui/core/IconButton"
import Button from "@material-ui/core/Button"
import TextField from "@material-ui/core/TextField"
import Checkbox from "@material-ui/core/Checkbox"
import MenuItem from "@material-ui/core/MenuItem"
import Dialog from "@material-ui/core/Dialog"
import DialogTitle from "@material-ui/core/DialogTitle"
import DialogContent from "@material-ui/core/DialogContent"
import DeleteIcon from "@material-ui/icons/DeleteOutline"
import EditIcon from "@material-ui/icons/Edit"
import AddIcon from "@material-ui/icons/Add"
import CheckIcon from "@material-ui/icons/Check"
import CloseIcon from "@material-ui/icons/Close"
import moment from "moment"
import { useTranslation } from "react-i18next"
import NumberFormat from "react-number-format"
import { validateCNPJ, validateCPF } from "../utils/utils"

const useStyles = makeStyles((theme) => ({
  tableStyle: {
    border: "none",
    boxShadow: "none",
  },
  tableText: {
    fontFamily: "Open Sans",
    fontWeight: "normal",
    fontSize: "12px",
    lineHeight: "16px",
    color: "#141414",
    textAlign: "center",
    margin: "10px",
    padding: "6px",
    minWidth: "100px",
  },
  tableHeader: {
    textAlign: "center",
    fontFamily: "Open Sans",
    fontStyle: "normal",
    fontWeight: "normal",
    color: "#6F6F6F",
    fontSize: "14px",
    lineHeight: "19px",
    padding: "6px",
  },
  even: {
    background: "#F3F3F3",
  },
  odd: {
    background: "#FFFFFF",
  },
  paginationContainer: {
    display: "flex",
    justifyContent: "center",
    marginTop: "10px",
  },
  badge: {
    borderRadius: "4px",
    padding: "2px 8px",
    fontSize: "11px",
    margin: "0 4px",
    display: "inline-block",
    cursor: "help",
    textAlign: "center",
    minWidth: "30px",
  },
  badgeGreen: {
    backgroundColor: "#6dd67d55",
    color: "#00C41D",
  },
  badgeRed: {
    backgroundColor: "#ff828255",
    color: "#A60505",
    fontWeight: "bold",
  },
  smallInput: {
    fontSize: "12px",
    "& .MuiInputBase-input": {
        fontSize: "12px",
        padding: "4px 0 5px",
        textAlign: "center"
    }
  },
  actionCell: {
      width: "90px",
      padding: "0px 8px",
      whiteSpace: "nowrap"
  }
}))

function OccurrenceInvolvedPartiesTable(props) {
  const { t } = useTranslation()
  const classes = useStyles()

  const { involvedParties = [], onChange, readOnly, regulatoryAuthority = 'bacen' } = props

  const pageSize = 10
  const [page, setPage] = useState(1)

  const [isAdding, setIsAdding] = useState(false)
  const [newRowData, setNewRowData] = useState({
      document_number: "",
      name: "",
      type: "",
      branch_number: "",
      branch_name: "",
      account_open_date: null,
      register_update_date: null,
      is_obligated_person: false,
      is_pep: false,
      public_employee_status: "not_public_employee",
      no_document: false
  })

  const [editIndex, setEditIndex] = useState(-1)
  const [editRowData, setEditRowData] = useState({})

  const [errorState, setErrorState] = useState({})
  const [hasShownNoDocumentDialog, setHasShownNoDocumentDialog] = useState(false)
  const [noDocumentInfoDialogOpen, setNoDocumentInfoDialogOpen] = useState(false)

  const generateKey = () => Math.random().toString(36).substring(2, 9) 

  const handleChangePage = (event, newPage) => {
    setPage(newPage)
    setEditIndex(-1)
    setErrorState({})
    setIsAdding(false)
  }

  const handleDelete = (indexToDelete) => {
      const actualIndex = (page - 1) * pageSize + indexToDelete
      const newList = [...involvedParties]
      newList.splice(actualIndex, 1)
      if (onChange) onChange(newList)
  }

  const handleStartAdd = () => {
      setPage(1)
      setIsAdding(true)
      setEditIndex(-1)
      setErrorState({})
      setNewRowData({
        document_number: "",
        name: "",
        type: "",
        branch_number: "",
        branch_name: "",
        account_number: "",
        account_open_date: "",
        register_update_date: "",
        is_obligated_person: false,
        is_pep: false,
        public_employee_status: "not_public_employee",
        no_document: false
      })
  }

  const handleCancelAdd = () => {
      setIsAdding(false)
      setErrorState({})
  }

  const validateRow = (data) => {
      const errors = {}
      const isCvmNoDocument = regulatoryAuthority === 'cvm' && data.no_document
      const cleanDoc = (data.document_number || "").replace(/\D/g, "")

      if (!isCvmNoDocument) {
          if (!cleanDoc) {
              errors.document_number = t("Obrigatório")
          } else if (cleanDoc.length <= 11) {
              if (!validateCPF(cleanDoc)) errors.document_number = t("CPF Inválido")
          } else {
              if (!validateCNPJ(cleanDoc)) errors.document_number = t("CNPJ Inválido")
          }
      }

      if (!data.name || data.name.trim() === "") errors.name = true
      if (!data.type) errors.type = true

      if (data.type === 'holder' && regulatoryAuthority !== 'cvm') {
          if (!data.branch_number) errors.branch_number = true
          if (!data.branch_name) errors.branch_name = true
          if (!data.account_number) errors.account_number = true
          if (!data.account_open_date) errors.account_open_date = true
          if (!data.register_update_date) errors.register_update_date = true
      }

      return errors
  }

  const cleanRowData = (data) => {
    if (data.branch_number === "") delete data.branch_number
    if (data.branch_name === "") delete data.branch_name
    if (data.account_number === "") delete data.account_number
    if (data.account_open_date === "") delete data.account_open_date
    if (data.register_update_date === "") delete data.register_update_date
    if (data.no_document && regulatoryAuthority === 'cvm') {
      data.document_number = ""
    } else if (data.no_document === false || regulatoryAuthority !== 'cvm') {
      delete data.no_document
    }
    return data
  }

  const handleConfirmAdd = () => {
      const errors = validateRow(newRowData)
      if (Object.keys(errors).length > 0) {
          setErrorState(errors)
          return
      }

      const cleanedData = cleanRowData(newRowData)
      const newList = [cleanedData, ...involvedParties]

      const newListWithKeys = newList.map(item => {
          if (!item._uiId) {
              return { ...item, _uiId: generateKey() }
          }
          return item
      })

      if (onChange) onChange(newListWithKeys)
      setIsAdding(false)
      setErrorState({})
  }

  const handleNewRowChange = (field, value) => {
      setNewRowData(prev => ({ ...prev, [field]: value }))
      if (errorState[field]) {
          setErrorState(prev => ({ ...prev, [field]: null }))
      }
  }

  const handleStartEdit = (index, party) => {
      setEditIndex(index)
      setIsAdding(false)
      setErrorState({})
      setEditRowData({ ...party })
  }

  const handleCancelEdit = () => {
      setEditIndex(-1)
      setEditRowData({})
      setErrorState({})
  }

  const handleConfirmEdit = () => {
      const errors = validateRow(editRowData)
      if (Object.keys(errors).length > 0) {
          setErrorState(errors)
          return
      }

      const actualIndex = (page - 1) * pageSize + editIndex
      const newList = [...involvedParties]
      const cleanedData = cleanRowData(editRowData)
      newList[actualIndex] = cleanedData

      if (onChange) onChange(newList)
      setEditIndex(-1)
      setErrorState({})
  }

  const handleEditRowChange = (field, value) => {
      setEditRowData(prev => ({ ...prev, [field]: value }))
      if (errorState[field]) {
          setErrorState(prev => ({ ...prev, [field]: null }))
      }
  }

  const startIndex = (page - 1) * pageSize
  const endIndex = page * pageSize
  const currentData = involvedParties.slice(startIndex, endIndex)
  const pageCount = Math.ceil(involvedParties.length / pageSize)

  const getRowClass = (index) => {
    return index % 2 === 0 ? classes.even : classes.odd
  }

  const renderStatusBadge = (label, isActive, activeText, inactiveText) => {
    const badgeClass = `${classes.badge} ${
      isActive ? classes.badgeGreen : classes.badgeRed
    }`
    const tooltipText = isActive ? activeText : inactiveText

    return (
      <Tooltip title={tooltipText} placement="top">
        <span className={badgeClass}>{label}</span>
      </Tooltip>
    )
  }

  const bacenPartyTypes = [
    "beneficiary",
    "depositor",
    "manager",
    "depository_institution",
    "paying_institution",
    "receiving_institution",
    "others",
    "legal_representative",
    "sender",
    "responsible_party",
    "drawer",
    "partner",
    "holder"
  ]

  const cvmPartyTypes = [
    "beneficiary",
    "transferor_asset_transfer",
    "assignee_asset_transfer",
    "client",
    "counterpart_trading_platform",
    "counterpart_operations_registry",
    "depositor",
    "loan_asset_lender",
    "manager_director",
    "intermediary_broker_distributor",
    "investor",
    "intermediary_operator",
    "others",
    "participant",
    "procurator_legal_representative",
    "responsible",
    "shareholder",
    "holder",
    "loan_asset_borrower"
  ]

  const involvedPartyTypes = regulatoryAuthority === 'cvm' ? cvmPartyTypes : bacenPartyTypes
  const cvmBranchDisabled = regulatoryAuthority === 'cvm'

  const renderInputCells = (data, handleChangeHandler, onConfirm, onCancel, rowClass, key) => {
      const hasDocument = data.document_number && String(data.document_number).trim()
      const noDocumentChecked = regulatoryAuthority === 'cvm' && (data.no_document === true || (!hasDocument && data.no_document !== false))
      return (
          <TableRow key={key} className={rowClass} style={{ border: "2px solid #2196f3" }}>
              <TableCell className={classes.tableText}>
                  <Tooltip title={errorState.document_number || ""} open={!!errorState.document_number} placement="top" arrow>
                    <div>
                        <NumberFormat
                            customInput={TextField}
                            placeholder="Doc"
                            value={data.document_number}
                            format={
                              data.document_number && data.document_number.replace(/\D/g, "").length > 11
                              ? "##.###.###/####-##"
                              : "###.###.###-#####"
                            }
                            onValueChange={(values) => {
                                handleChangeHandler("document_number", values.formattedValue)
                            }}
                            error={!!errorState.document_number}
                            className={classes.smallInput}
                            disabled={regulatoryAuthority === 'cvm' && noDocumentChecked}
                        />
                        {regulatoryAuthority === 'cvm' && (
                            <div style={{ display: 'flex', alignItems: 'center', marginTop: '4px' }}>
                                <Checkbox
                                    checked={!!noDocumentChecked}
                                    onChange={(e) => {
                                        const checked = e.target.checked
                                        if (checked && !hasShownNoDocumentDialog) {
                                            setHasShownNoDocumentDialog(true)
                                            setNoDocumentInfoDialogOpen(true)
                                        }
                                        handleChangeHandler("no_document", checked)
                                        if (checked) {
                                            handleChangeHandler("document_number", "")
                                            if (errorState.document_number) setErrorState(prev => ({ ...prev, document_number: null }))
                                        }
                                    }}
                                    color="primary"
                                    size="small"
                                    style={{ padding: 0 }}
                                />
                                <span className={classes.smallInput} style={{ marginLeft: 4 }}>{t("Sem documento")}</span>
                            </div>
                        )}
                    </div>
                  </Tooltip>
              </TableCell>

              <TableCell className={classes.tableText}>
                  <TextField
                    placeholder="Nome"
                    value={data.name}
                    onChange={(e) => handleChangeHandler("name", e.target.value)}
                    error={!!errorState.name}
                    className={classes.smallInput}
                  />
              </TableCell>

              <TableCell className={classes.tableText}>
                  <TextField
                    select
                    value={data.type}
                    onChange={(e) => handleChangeHandler("type", e.target.value)}
                    error={!!errorState.type}
                    className={classes.smallInput}
                    fullWidth
                    SelectProps={{
                        displayEmpty: true,
                        renderValue: (value) => value ? t(value) : <span style={{ color: "#aaa" }}>Tipo</span>
                    }}
                  >
                    {involvedPartyTypes.map((option) => (
                        <MenuItem key={option} value={option}>
                            {t(option)}
                        </MenuItem>
                    ))}
                  </TextField>
              </TableCell>

              <TableCell className={classes.tableText}>
                  <div style={{display:'flex', gap: '4px'}}>
                    <NumberFormat
                        customInput={TextField}
                        placeholder="Nº Ag"
                        value={data.branch_number}
                        format="####"
                        onValueChange={(values) => {
                            handleChangeHandler("branch_number", values.value)
                        }}
                        onBlur={() => {
                            if (data.branch_number) {
                                handleChangeHandler("branch_number", data.branch_number.padStart(4, "0"))
                            }
                        }}
                        error={!!errorState.branch_number}
                        className={classes.smallInput}
                        style={{width: '40px'}}
                        disabled={cvmBranchDisabled}
                    />
                    <TextField
                        placeholder="Nome Ag"
                        value={data.branch_name}
                        onChange={(e) => handleChangeHandler("branch_name", e.target.value)}
                        error={!!errorState.branch_name}
                        className={classes.smallInput}
                        disabled={cvmBranchDisabled}
                    />
                  </div>
              </TableCell>
              <TableCell className={classes.tableText}>
                  <TextField
                    placeholder={t("Nº Conta")}
                    value={data.account_number}
                    onChange={(e) => handleChangeHandler("account_number", e.target.value)}
                    error={!!errorState.account_number}
                    className={classes.smallInput}
                    disabled={cvmBranchDisabled}
                  />
              </TableCell>

              <TableCell className={classes.tableText}>
                 <TextField
                    type="date"
                    value={data.account_open_date ? moment(data.account_open_date).format("YYYY-MM-DD") : ""}
                    onChange={(e) => handleChangeHandler("account_open_date", e.target.value ? moment(e.target.value).toISOString() : null)}
                    error={!!errorState.account_open_date}
                    className={classes.smallInput}
                    disabled={cvmBranchDisabled}
                 />
              </TableCell>
              <TableCell className={classes.tableText}>
                 <TextField
                    type="date"
                    value={data.register_update_date ? moment(data.register_update_date).format("YYYY-MM-DD") : ""}
                    onChange={(e) => handleChangeHandler("register_update_date", e.target.value ? moment(e.target.value).toISOString() : null)}
                    error={!!errorState.register_update_date}
                    className={classes.smallInput}
                    disabled={cvmBranchDisabled}
                 />
              </TableCell>
              <TableCell className={classes.tableText}>
                  <div style={{ display: 'flex', justifyContent: 'center', gap: '5px' }}>
                    <Tooltip title="PO">
                        <Checkbox
                            checked={data.is_obligated_person}
                            onChange={(e) => handleChangeHandler("is_obligated_person", e.target.checked)}
                            color="primary"
                            size="small"
                            style={{padding: 0}}
                        />
                    </Tooltip>
                    <Tooltip title="PEP">
                        <Checkbox
                            checked={data.is_pep}
                            onChange={(e) => handleChangeHandler("is_pep", e.target.checked)}
                            color="primary"
                            size="small"
                            style={{padding: 0}}
                        />
                    </Tooltip>
                    <Tooltip title="SP">
                        <Checkbox
                            checked={data.public_employee_status !== "not_public_employee"}
                            onChange={(e) => handleChangeHandler("public_employee_status", e.target.checked ? "public_employee" : "not_public_employee")}
                            color="primary"
                            size="small"
                            style={{padding: 0}}
                        />
                    </Tooltip>
                  </div>
              </TableCell>
              <TableCell className={classes.actionCell}>
                  <div style={{ display: 'flex', justifyContent: 'center' }}>
                    <IconButton size="small" onClick={onConfirm} style={{ color: "green" }}>
                        <CheckIcon fontSize="small" />
                    </IconButton>
                    <IconButton size="small" onClick={onCancel} style={{ color: "red" }}>
                        <CloseIcon fontSize="small" />
                    </IconButton>
                  </div>
              </TableCell>
          </TableRow>
      )
  }

  const isAddButtonEnabled = (!readOnly && !isAdding && editIndex === -1)

  return (
    <div>
        <div style={{ display: 'flex', justifyContent: 'flex-start', marginBottom: '8px', paddingLeft: '8px' }}>
            <Button
            disableRipple
            startIcon={<AddIcon />}
            onClick={handleStartAdd}
            size="small"
            style={{ textTransform: 'none', fontWeight: 'bold', color: isAddButtonEnabled ? '#0b1f82' : '#0b1f8236' }}
            disabled={!isAddButtonEnabled}
            >
                {t("Adicionar parte envolvida")}
            </Button>
        </div>

      <Paper className={classes.tableStyle}>
        <Table style={{ padding: "8px" }}>
          <TableHead>
            <TableRow>
              <TableCell className={classes.tableHeader}>{t("Nº Documento")}</TableCell>
              <TableCell className={classes.tableHeader}>{t("Nome")}</TableCell>
              <TableCell className={classes.tableHeader}>{t("Tipo de Envolvido")}</TableCell>
              <TableCell className={classes.tableHeader}>{t("Agência")}</TableCell>
              <TableCell className={classes.tableHeader}>{t("Nº Conta")}</TableCell>
              <TableCell className={classes.tableHeader}>{t("Abertura da Conta")}</TableCell>
              <TableCell className={classes.tableHeader}>{t("Última Atualização")}</TableCell>
              <TableCell className={classes.tableHeader}>{t("Status")}</TableCell>
              {!readOnly && <TableCell className={classes.tableHeader}></TableCell>}
            </TableRow>
          </TableHead>

          <TableBody>
            {isAdding && renderInputCells(newRowData, handleNewRowChange, handleConfirmAdd, handleCancelAdd, classes.odd, "add-row-key")}

            {(!involvedParties || involvedParties.length === 0) && !isAdding ? (
               <TableRow>
                   <TableCell colSpan={8}>
                        <div className="emptyContainer" style={{ padding: "10px", textAlign: "center" }}>
                            <p className={["blueText", "subtitleSize"].join(" ")}>
                            {t("Nenhuma parte envolvida")}
                            </p>
                        </div>
                   </TableCell>
               </TableRow>
            ) : (
                currentData.map((party, index) => {
                    const tableRowKey = party._uiId
                    if (index === editIndex && !isAdding) {
                        return renderInputCells(editRowData, handleEditRowChange, handleConfirmEdit, handleCancelEdit, getRowClass(index), tableRowKey)
                    }

                    const isPO = party.is_obligated_person
                    const isPEP = party.is_pep
                    const isSP = party.public_employee_status !== "not_public_employee"

                    return (
                        <TableRow
                        key={party._uiId || index}
                        className={getRowClass(index)}
                        >
                        <TableCell className={classes.tableText}>
                            {regulatoryAuthority === 'cvm' && party.no_document ? t("Sem documento") : (party.document_number || "-")}
                        </TableCell>
                        <TableCell className={classes.tableText}>
                            {party.name}
                        </TableCell>
                        <TableCell className={classes.tableText}>
                            {t(party.type)}
                        </TableCell>
                        <TableCell className={classes.tableText}>
                            {`${party.branch_number || ""} - ${party.branch_name || ""}`}
                        </TableCell>
                        <TableCell className={classes.tableText}>
                            {party.account_number || ""}
                        </TableCell>
                        <TableCell className={classes.tableText}>
                            {party.account_open_date ? moment(party.account_open_date).format("DD/MM/YYYY") : "-"}
                        </TableCell>
                        <TableCell className={classes.tableText}>
                            {party.register_update_date ? moment(party.register_update_date).format("DD/MM/YYYY") : "-"}
                        </TableCell>
                        <TableCell className={classes.tableText} style={{ minWidth: "160px" }}>
                            {renderStatusBadge("PO", isPO, `${t("É")} ${t("pessoa obrigada")}`, `${t("Não é")} ${t("pessoa obrigada")}`)}
                            {renderStatusBadge("PEP", isPEP, `${t("É")} ${t("pessoa exposta politicamente")}`, `${t("Não é")} ${t("pessoa exposta politicamente")}`)}
                            {renderStatusBadge("SP", isSP, `${t("É")} ${t("servidor público")}`, `${t("Não é")} ${t("servidor público")}`)}
                        </TableCell>

                        {!readOnly && (
                            <TableCell className={classes.actionCell}>
                                <div style={{ display: 'flex', justifyContent: 'center' }}>
                                    <Tooltip title={t("Editar")} placement="top">
                                        <span>
                                            <IconButton
                                                size="small"
                                                onClick={() => handleStartEdit(index, party)}
                                                disabled={isAdding || editIndex !== -1}
                                                style={{ opacity: (isAdding || editIndex !== -1) ? 0.3 : 1 }}
                                            >
                                                <EditIcon fontSize="small" style={{ color: "#0b1f82" }} />
                                            </IconButton>
                                        </span>
                                    </Tooltip>

                                    <Tooltip title={t("Remover")} placement="top">
                                        <span>
                                            <IconButton
                                                size="small"
                                                onClick={() => handleDelete(index)}
                                                disabled={isAdding || editIndex !== -1}
                                                style={{ opacity: (isAdding || editIndex !== -1) ? 0.3 : 1 }}
                                            >
                                                <DeleteIcon fontSize="small" />
                                            </IconButton>
                                        </span>
                                    </Tooltip>
                                </div>
                            </TableCell>
                        )}
                        </TableRow>
                    )
                })
            )}
          </TableBody>
        </Table>
      </Paper>

      {involvedParties && involvedParties.length > 0 && (
        <div className={classes.paginationContainer}>
            <Pagination
            className="pagination"
            page={page}
            count={pageCount}
            onChange={handleChangePage}
            />
        </div>
      )}

      <Dialog open={noDocumentInfoDialogOpen} onClose={() => setNoDocumentInfoDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle disableTypography>
          <div style={{ textAlign: "center", color: "#0b1f82", fontWeight: "bold", fontSize: "20px" }}>
            {t("Sem documento")}
          </div>
        </DialogTitle>
        <DialogContent>
          <div
            style={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              gap: "20px"
            }}
          >
            <div className="internalCardContainer normalText dialogInfoSize" style={{ whiteSpace: "pre-wrap", fontWeight: "normal", textAlign: "justify", width: "100%", maxWidth: "100%" }}>
              {t("coaf_no_document_info_dialog_text")}
            </div>
            <div style={{ width: "100%", display: "flex", justifyContent: "flex-end" }}>
              <Button
                onClick={() => setNoDocumentInfoDialogOpen(false)}
                variant="contained"
                style={{ backgroundColor: "#0b1f82", color: "#fff" }}
              >
                {t("coaf_no_document_info_dialog_understand")}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  )
}

export default OccurrenceInvolvedPartiesTable